import { getCurrentInstance, onBeforeUnmount, ref, watch, type Ref, type WatchSource } from 'vue';

/** A point in time: epoch milliseconds, an ISO string or a `Date`. */
export type NxTimestamp = number | string | Date;

/** Epoch milliseconds of a timestamp; `null` for nothing or an unparseable value. */
export function toEpochMs(value: NxTimestamp | null | undefined): number | null {
  if (value == null || value === '') return null;
  const ms =
    value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Whole seconds on a timer: the banked `accumulatedSeconds` plus, while
 * `running`, the time since `startedAt`. Never negative — a start in the
 * future (clock skew) counts as zero.
 */
export function elapsedSeconds(
  startedAt: NxTimestamp | null | undefined,
  accumulatedSeconds = 0,
  now = Date.now(),
  running = true
): number {
  const start = running ? toEpochMs(startedAt) : null;
  const live = start == null ? 0 : Math.max(0, now - start) / 1000;
  return Math.max(0, Math.floor((accumulatedSeconds || 0) + live));
}

/** „H:MM:SS“ with open-ended hours (`123:04:05`); `padHours` gives „HH:MM:SS“. */
export function formatElapsed(totalSeconds: number, options: { padHours?: boolean } = {}): string {
  const s = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  const hours = String(Math.floor(s / 3600));
  const minutes = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const seconds = String(s % 60).padStart(2, '0');
  return `${options.padHours ? hours.padStart(2, '0') : hours}:${minutes}:${seconds}`;
}

/** ISO 8601 duration for `<time datetime>`, e.g. `PT1H2M3S`. */
export function isoDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  return `PT${Math.floor(s / 3600)}H${Math.floor((s % 3600) / 60)}M${s % 60}S`;
}

/**
 * `now` (epoch ms) that advances once a second while `active` is true.
 *
 * The ticks land on the second boundaries of `anchor` (a start time), so a
 * clock derived from it never skips or repeats a second the way a drifting
 * `setInterval` does. It stops on unmount and catches up at once when a
 * hidden tab becomes visible again (browsers throttle background timers).
 */
export function useSecondTicker(
  active: WatchSource<boolean>,
  anchor: WatchSource<number | null> = () => null
): Ref<number> {
  const now = ref(Date.now());
  let handle: ReturnType<typeof setTimeout> | undefined;
  let anchorMs: number | null = null;

  function clear() {
    if (handle === undefined) return;
    clearTimeout(handle);
    handle = undefined;
  }

  function schedule() {
    clear();
    const offset = (((Date.now() - (anchorMs ?? 0)) % 1000) + 1000) % 1000;
    handle = setTimeout(tick, 1000 - offset);
  }

  function tick() {
    handle = undefined;
    now.value = Date.now();
    schedule();
  }

  function onVisibilityChange() {
    if (handle === undefined || document.visibilityState !== 'visible') return;
    now.value = Date.now();
    schedule();
  }

  watch(
    [active, anchor],
    ([on, at]) => {
      anchorMs = at ?? null;
      if (!on) {
        clear();
        return;
      }
      now.value = Date.now();
      schedule();
    },
    { immediate: true }
  );

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      clear();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    });
  }
  return now;
}
